import { useCallback, useEffect, useReducer, useState } from "react";
import { VideoPlaybackAction, VideoPlaybackState } from "../../student-selection/videoplayer/ProctoringResultView";
import { Typography } from "@mui/material";
import "../../student-selection/videoplayer/ProctoringResultView.css";
import ProctoringVideoPlayer, {
	SessionNote,
	VideoSettings,
} from "../../student-selection/videoplayer/ProctoringVideoPlayer";
import { CoreModal, CoreTextField } from "@verity/components/core";
import apiClient from "../../../../helpers/api-client";
import { DownloadRequestStatusType } from "../../student-selection/videoplayer/DownloadRequestModal";
import useTimestamps, { TimestampsArr, VideoEvent } from "../../../utilities/useTimestamps";

type LiveProctoringSessionNote = SessionNote & {
	id: string;
};

export default function VideoPlayer({
	sessionId,
	webcamVidSources = {},
	screenVidSources = {},
	duration,
	timeOffset = 0,
	startDate = new Date(0),
	audioOnly = false,
	hideCameraVideo = false,
	events = [],
	sessionEvents = [],
	liveNotes = [],
	className,
}: {
	sessionId: string;
	webcamVidSources?: Record<string, string>;
	screenVidSources?: Record<string, string>;
	duration?: number;
	timeOffset?: number;
	startDate?: Date;
	audioOnly?: boolean;
	hideCameraVideo?: boolean;
	events?: VideoEvent[];
	sessionEvents?: VideoEvent[];
	liveNotes?: LiveProctoringSessionNote[];
	className?: string;
}) {
	let screenAvail = Object.keys(screenVidSources).length > 0;
	let webcamAvail = Object.keys(webcamVidSources).length > 0;
	const showVideoUnavailable = !screenAvail && !webcamAvail;

	const [inFullscreen, setInFullscreen] = useState(false);
	const [createdObserver, setCreatedObserver] = useState(false);
	const [stringExtend, setStringExtend] = useState("_fs");
	const [showingVideoTitles, setShowingVideoTitles] = useState(true);

	const setFullscreen = useCallback(() => {
		// The page is not in an iframe
		if (!document.fullscreenElement) {
			void document.getElementById("entire_player")?.requestFullscreen();
			// setStringExtend("_fs");
			if (!createdObserver) {
				setCreatedObserver(true);
				makeObserver();
			}
		} else {
			void document.exitFullscreen();
		}
	}, [createdObserver]);

	// Makes an observer to check when the videos are in fullscreen or not.
	function makeObserver() {
		// Select the node that will be observed for mutations
		const targetNode = document.getElementById("entire_player");

		// Options for the observer (which mutations to observe)
		const config = { attributes: true, childList: true, subtree: true };

		// Callback function to execute when mutations are observed
		const callback = (mutationList: { type: string }[], _observer: MutationObserver) => {
			for (const mutation of mutationList) {
				if (mutation.type === "attributes") {
					if (document.fullscreenElement) {
						setInFullscreen(true);
					} else {
						setInFullscreen(false);
					}
				}
			}
		};

		// Create an observer instance linked to the callback function
		const observer = new MutationObserver(callback);

		// Start observing the target node for configured mutations
		if (targetNode) {
			observer.observe(targetNode, config);
		}
		// Later, you can stop observing
		// observer.disconnect();
	}

	function modifyFSMode(changedMode: string) {
		document.querySelectorAll("#fullscreenMenuHolder .hide").forEach((e) => {
			e.classList.remove("hide");
		});
		switch (changedMode) {
			case "_fs":
				document.getElementById("Centered_fs")?.classList.add("hide");
				break;
			case "_fs_lm":
				document.getElementById("Left_and_Minimized_fs")?.classList.add("hide");
				break;
			case "_fs_rm":
				document.getElementById("Right_and_Minimized_fs")?.classList.add("hide");
				break;
			case "_fs_l":
				document.getElementById("Left_fs")?.classList.add("hide");
				break;
			case "_fs_r":
				document.getElementById("Right_fs")?.classList.add("hide");
				break;
		}
		setStringExtend(changedMode);
	}

	const [controlOptions, setControlOptions] = useState({
		isPlaying: false,
		playbackSpeed: 1,
		volume: 0.5,
	});
	const reducer = (state: VideoPlaybackState, action: VideoPlaybackAction) => {
		if (duration) {
			switch (action.type) {
				case "play":
					if (state.timer < duration) {
						return {
							timer: state.timer + (performance.now() - state.lastTime) * action.speed,
							lastTime: performance.now(),
							skip: false,
						};
					} else {
						setControlOptions((controlOptions) => ({
							...controlOptions,
							isPlaying: !controlOptions.isPlaying,
						}));
						return { timer: state.timer, lastTime: performance.now(), skip: false };
					}
				case "pause":
					return { timer: state.timer, lastTime: performance.now(), skip: false };
				case "skip":
					return {
						timer: Math.max(timeOffset, Math.min(Math.round(action.time), duration)),
						lastTime: performance.now(),
						skip: true,
					};
			}
		}
		return state;
	};

	const [playback, playbackDispatch] = useReducer(reducer, { timer: 0, lastTime: performance.now(), skip: false });

	useEffect(() => {
		playbackDispatch({ type: "skip", time: timeOffset });
	}, [timeOffset]);

	useEffect(() => {
		if (!controlOptions.isPlaying) {
			setShowingVideoTitles(true);
		} else {
			setShowingVideoTitles(false);
			if (playback.timer >= (duration ?? 0)) {
				playbackDispatch({ type: "skip", time: timeOffset });
			}
			const interval = setInterval(() => {
				playbackDispatch({ type: "play", speed: controlOptions.playbackSpeed });
			}, 1000 / 60);
			return () => clearInterval(interval);
		}
	}, [controlOptions.isPlaying, controlOptions.playbackSpeed]);

	const setPlay = () => {
		// Used to reset the timer otherwise a paused video's time will continue to go up even when the video is paused (so unpausing it will cause a skip)
		// this will update the timer the second the video starts playing making sure that the calculations are correct (updating performance.now to the most recent time)
		playbackDispatch({ type: "pause" });
		setControlOptions((controlOptions) => ({
			...controlOptions,
			isPlaying: !controlOptions.isPlaying,
		}));
		const pauseImage = document.getElementById("pauseIconImage");
		setTimeout(() => {
			pauseImage?.setAttribute("style", "display: none");
		}, 250);
	};

	const setVolume: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		setControlOptions((controlOptions) => ({
			...controlOptions,
			volume: Number(e.target.value),
		}));
	};

	const setSpeed = (value: string) => {
		const num = parseFloat(value);
		setControlOptions((controlOptions) => ({
			...controlOptions,
			playbackSpeed: num,
		}));
	};

	useEffect(() => {
		const handleKeyboard = (event: KeyboardEvent) => {
			const playerElement = document.getElementById("entire_player");
			const focusedElement = document.activeElement;
			if (focusedElement?.closest(".MuiDialog-container") !== playerElement?.closest(".MuiDialog-container")) {
				return;
			}
			if (webCamHasError && screenCaptureHasError) {
				if (event.key === "f" || event.key === "F") {
					setFullscreen();
					return;
				}
			}
			if (event.key === " ") {
				if (focusedElement?.tagName.toLowerCase() !== "button") {
					setPlay();
				}
			} else if (event.key === "f") {
				setFullscreen();
			} else if (event.key === "ArrowRight") {
				playbackDispatch({ type: "skip", time: playback.timer + 10000 });
			} else if (event.key === "ArrowLeft") {
				playbackDispatch({ type: "skip", time: playback.timer - 10000 });
			}
		};
		document.addEventListener("keydown", handleKeyboard);
		return () => document.removeEventListener("keydown", handleKeyboard);
	}, [setFullscreen, playback.timer]);

	const [showFullscreenButtons, setShowFullscreenButtons] = useState(false);
	document.addEventListener("click", function handleClickOutsideBox(event) {
		if (showFullscreenButtons) {
			const button = document.getElementById("fullscreenModeButton");
			if (button?.contains(event.target as Node)) {
				return;
			} else {
				setShowFullscreenButtons(false);
			}
		}
	});

	const { screenTimestampsArr, webcamTimestampsArr, setTimestampEvents } = useTimestamps({
		videoEvents: sessionEvents,
	});
	useEffect(() => {
		setTimestampEvents(sessionEvents);
	}, [sessionEvents]);

	const shouldShowDownload = ((
		Object.keys(webcamVidSources).length > 0 &&
		!Object.values(webcamVidSources).some((source) => source === "processing")
	) || (
		Object.keys(screenVidSources).length > 0 &&
		!Object.values(screenVidSources).some((source) => source === "processing")
	));

	const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
	const [userEnteredEmail, setUserEnteredEmail] = useState("");
	const [userEnteredName, setUserEnteredName] = useState("");
	const [downloadRequestStatus, setDownloadRequestStatus] = useState<DownloadRequestStatusType>("unsent");

	const [webCamHasError, setWebCamHasError] = useState(false);
	const [screenCaptureHasError, setScreenCaptureHasError] = useState(false);

	const visibleLiveNotes = liveNotes;

	const videoJsOptions = {
		controls: true,
		responsive: true,
		aspectRatio: "16:9",
		fluid: true,
		userActions: {
			click: false,
		},
		controlBar: {
			playToggle: false,
			volumePanel: false,
			progressControl: false,
			remainingTimeDisplay: false,
			pictureInPictureToggle: false,
			fullscreenToggle: false,
		},
		bigPlayButton: false,
	} as VideoSettings;

	const sourcesMatchTimestamps = (sources: Record<string, string>, timestamps: TimestampsArr) =>
		Object.keys(sources).every((sourceKey) => timestamps.some(({ vid }) => String(vid) === sourceKey));
	return (
		<div style={{ display: "flex", height: "fit-content" }} className={className}>
			<div className={!inFullscreen ? "entire_player" : `entire_player_fs`} id="entire_player">
				<ProctoringVideoPlayer
					isFullscreen={inFullscreen}
					sessionId={sessionId}
					showTimestamps={false}
					controlOptions={controlOptions}
					playback={playback}
					playbackDispatch={playbackDispatch}
					webcamVidSources={sourcesMatchTimestamps(webcamVidSources, webcamTimestampsArr) ? webcamVidSources : {}}
					screenVidSources={sourcesMatchTimestamps(screenVidSources, screenTimestampsArr) ? screenVidSources : {}}
					startDate={startDate}
					duration={duration}
					timeOffset={timeOffset}
					setFullscreen={setFullscreen}
					setPlay={setPlay}
					setVolume={setVolume}
					setSpeed={setSpeed}
					setShowEmailModal={setShowEmailModal}
					setUserEnteredEmail={setUserEnteredEmail}
					setUserEnteredName={setUserEnteredName}
					downloadRequestStatus={downloadRequestStatus}
					setDownloadRequestStatus={setDownloadRequestStatus}
					shouldShowDownload={shouldShowDownload}
					showVideoUnavailable={showVideoUnavailable}
					showAudioThumbnail={audioOnly}
					showingVideoTitles={showingVideoTitles}
					webcamVideoOptions={videoJsOptions}
					screencaptureVideoOptions={videoJsOptions}
					shouldHideCamVid={hideCameraVideo}
					modifyFSMode={modifyFSMode}
					stringExtend={stringExtend}
					events={events}
					screenTimestampsArr={screenTimestampsArr}
					webcamTimestampsArr={webcamTimestampsArr}
					sessionNotes={[]}
					visibleLiveNotes={visibleLiveNotes}
					webCamHasError={webCamHasError}
					setWebCamHasError={setWebCamHasError}
					screenCaptureHasError={screenCaptureHasError}
					setScreenCaptureHasError={setScreenCaptureHasError}
				/>
			</div>
			<CoreModal
				maxWidth="min(50%, 600px)"
				open={showEmailModal}
				onClose={() => setShowEmailModal(false)}
				title={"Email Recording"}
				buttons={[
					{
						text: "Cancel",
						color: "secondary",
						onClick: () => {
							setShowEmailModal(false);
						},
					},
					{
						text: "Send",
						color: "primary",
						onClick: () => {
							setDownloadRequestStatus("sending");
							setShowEmailModal(false);
							apiClient.download
								.requestProctoringSessionRecordingDownload(sessionId, userEnteredEmail, userEnteredName)
								.then(({ ready, emailDestination }) => {
									setDownloadRequestStatus({ status: "sent", ready, emailDestination });
								})
								.catch((err) => {
									console.error(err);
									setDownloadRequestStatus("failed");
								});
						},
					},
				]}
			>
				<div>
					<Typography>Enter the name and email you would like to send the recording to.</Typography>

					<div style={{ marginTop: "10px" }}>
						<Typography
							style={{
								fontSize: "15px",
								lineHeight: "24.38px",
								color: "black",
								marginBottom: "10px",
								fontWeight: "bold",
							}}
						>
							Full Name
						</Typography>
						<CoreTextField
							required
							sx={{ width: "100%" }}
							type="name"
							value={userEnteredName}
							onChange={(e) => {
								setUserEnteredName(e.target.value);
							}}
						/>
					</div>
					<div style={{ marginTop: "10px" }}>
						<Typography
							style={{
								fontSize: "15px",
								lineHeight: "24.38px",
								color: "black",
								marginBottom: "10px",
								fontWeight: "bold",
							}}
						>
							Email
						</Typography>
						<CoreTextField
							required
							sx={{ width: "100%" }}
							type="email"
							value={userEnteredEmail}
							onChange={(e) => {
								setUserEnteredEmail(e.target.value);
							}}
						/>
					</div>
				</div>
			</CoreModal>
		</div>
	);
}
